import React, { useRef } from "react";
import { shareStatus } from "../components/projectPages.js";
import { EXPORT_TABS, tabAfter, type ExportTab } from "./exportView.js";

const LABEL: Record<ExportTab, string> = { download: "Download", share: "Share" };

/**
 * The Export page's side panel: Download and Share as tabs beside the
 * preview, so what was made stays in view whichever is open. The page owns
 * which tab is open (`?tab=share` opens Share).
 */
export const ExportPanel: React.FC<{
  tab: ExportTab;
  onTab: (tab: ExportTab) => void;
  /** Published, not, or not known yet: the Share tab says Live or Draft. */
  live: boolean | null;
  panels: Record<ExportTab, React.ReactNode>;
}> = ({ tab, onTab, live, panels }) => {
  const buttons = useRef<Partial<Record<ExportTab, HTMLButtonElement | null>>>({});

  const onKeyDown = (event: React.KeyboardEvent) => {
    const next = tabAfter(tab, event.key);
    if (!next) return;
    event.preventDefault();
    onTab(next);
    buttons.current[next]?.focus();
  };

  return (
    <section
      id="share"
      aria-label="Export and share"
      className="panel flex min-w-0 max-w-[460px] flex-[1_1_380px] scroll-mt-4 flex-col self-start"
    >
      <div role="tablist" aria-label="Export" className="flex gap-1 border-b border-[var(--edge)] px-2 pt-2">
        {EXPORT_TABS.map((one) => {
          const selected = one === tab;
          return (
            <button
              key={one}
              ref={(element) => {
                buttons.current[one] = element;
              }}
              type="button"
              role="tab"
              id={`export-tab-${one}`}
              aria-selected={selected}
              aria-controls={`export-panel-${one}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onTab(one)}
              onKeyDown={onKeyDown}
              className={`-mb-px inline-flex items-center rounded-t-[6px] border-b-2 px-3.5 py-3 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)] text-[14px] font-semibold transition-colors ${
                selected ? "border-ember-400 text-ink-100" : "border-transparent text-ink-300 hover:text-ink-100"
              }`}
            >
              {LABEL[one]}
              {one === "share" && shareStatus(live) ? (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${shareStatus(live)!.className}`}
                >
                  {shareStatus(live)!.label}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {EXPORT_TABS.map((one) => (
        // Every panel stays mounted, hidden when not chosen: Share keeps
        // what it loaded, and an export keeps running on either tab.
        <div
          key={one}
          role="tabpanel"
          id={`export-panel-${one}`}
          aria-labelledby={`export-tab-${one}`}
          hidden={one !== tab}
          className="p-5"
        >
          {panels[one]}
        </div>
      ))}
    </section>
  );
};
